// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import AppDialog from './AppDialog'

/**
 * D-UI-3 — confirm before an irreversible or audited action. Replaces `window.confirm` / `prompt` and
 * one-click destructive buttons (CareRx audit C2, C6, E7, O12, O17, O18, O27).
 *
 * - `requireReason`: Confirm stays disabled until the reason has `minLength` non-space characters.
 * - `typedConfirm`: Confirm stays disabled until that exact word is typed (no stray double-Enter).
 * The reason goes ONLY to `onConfirm` — the caller sends it inside its audited request. It is never
 * logged here and is cleared whenever the dialog opens.
 */
export interface ConfirmDialogProps {
  open: boolean
  title: ReactNode
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
  requireReason?: { label: string; minLength?: number; placeholder?: string }
  typedConfirm?: string
  busy?: boolean
  /** A failure from the caller's request, shown inside the dialog (the page behind is covered). */
  error?: string | null
  /** An extra condition of the caller's own (e.g. a required checkbox in `body`). */
  confirmDisabled?: boolean
  /** Confirm label while busy (default "Working…"). */
  busyLabel?: string
  /** Width for a confirmation with sections (D-CRX-89 impact warning). Default 'xs', as before. */
  maxWidth?: 'xs' | 'sm' | 'md'
  onConfirm: (result: { reason: string }) => void
  onCancel: () => void
}

export function canConfirm(reason: string, typed: string, opts: Pick<ConfirmDialogProps, 'requireReason' | 'typedConfirm'>): boolean {
  if (opts.requireReason && reason.trim().length < Math.max(1, opts.requireReason.minLength ?? 1)) return false
  if (opts.typedConfirm && typed !== opts.typedConfirm) return false
  return true
}

export default function ConfirmDialog({
  open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'default',
  requireReason, typedConfirm, busy = false, error, confirmDisabled = false, busyLabel = 'Working…', maxWidth = 'xs', onConfirm, onCancel,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState('')
  const [typed, setTyped] = useState('')
  // One confirm per attempt. `busy` reaches us only after the caller re-renders, so a fast double-click
  // would land twice (Hold pilot H1 caught it). Re-armed when the caller's busy goes true -> false, when the
  // dialog opens or closes, or — for a caller that never sets busy — shortly after, so Confirm cannot stay dead.
  const submitted = useRef(false)
  const sawBusy = useRef(false)
  const rearm = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => { setReason(''); setTyped(''); submitted.current = false; sawBusy.current = false }, [open])
  useEffect(() => {
    if (busy) { sawBusy.current = true; return }
    if (sawBusy.current) { sawBusy.current = false; submitted.current = false }
  }, [busy])
  useEffect(() => () => { if (rearm.current) clearTimeout(rearm.current) }, [])
  const ok = canConfirm(reason, typed, { requireReason, typedConfirm }) && !busy && !confirmDisabled
  const submit = () => {
    if (!ok || submitted.current) return
    submitted.current = true
    if (rearm.current) clearTimeout(rearm.current)
    rearm.current = setTimeout(() => { if (!sawBusy.current) submitted.current = false }, 600)
    onConfirm({ reason: reason.trim() })
  }
  return (
    <AppDialog open={open} title={title} onClose={onCancel} busy={busy} role={tone === 'danger' ? 'alertdialog' : 'dialog'} maxWidth={maxWidth}
      actions={<>
        <Button variant="outlined" color="inherit" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
        <Button variant="contained" color={tone === 'danger' ? 'error' : 'primary'} onClick={submit} disabled={!ok}>
          {busy ? busyLabel : confirmLabel}
        </Button>
      </>}>
      {body}
      {requireReason && (
        <TextField autoFocus fullWidth multiline minRows={2} sx={{ mt: body ? 2 : 0 }}
          label={requireReason.label} placeholder={requireReason.placeholder}
          value={reason} onChange={e => setReason(e.target.value)} disabled={busy}
          helperText={requireReason.minLength && requireReason.minLength > 1 ? `At least ${requireReason.minLength} characters` : undefined} />
      )}
      {typedConfirm && (
        <TextField autoFocus={!requireReason} fullWidth sx={{ mt: 2 }} value={typed} disabled={busy}
          onChange={e => setTyped(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit() }}
          label={`Type ${typedConfirm} to confirm`} autoComplete="off" spellCheck={false}
          inputProps={{ 'aria-label': `Type ${typedConfirm} to confirm` }} />
      )}
      {error && <div role="alert" style={{ marginTop: 12, fontSize: 12.5, color: '#7f1d1d', background: '#fef2f2', border: '1px solid #f0a8a8', borderRadius: 8, padding: '8px 11px' }}>{error}</div>}
    </AppDialog>
  )
}
